import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import {
  Check, X, Clock, AlertCircle, FileText, Send, Edit2, ArrowRight,
  History, Receipt, MessageSquare, ChevronDown, ChevronUp, Users, Building2, ExternalLink
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatDate } from '@/utils/dateFormat';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-stone-100 text-stone-600', icon: Clock },
  sent: { label: 'Sent', color: 'bg-blue-100 text-blue-600', icon: Send },
  pending: { label: 'Pending Review', color: 'bg-yellow-100 text-yellow-600', icon: Clock },
  accepted: { label: 'Accepted', color: 'bg-green-100 text-green-600', icon: Check },
  partially_accepted: { label: 'Partially Accepted', color: 'bg-amber-100 text-amber-600', icon: Check },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-600', icon: X },
  revision_requested: { label: 'Revision Requested', color: 'bg-purple-100 text-purple-600', icon: Edit2 },
  expired: { label: 'Expired', color: 'bg-orange-100 text-orange-600', icon: AlertCircle },
};

const ITEM_STATUS = {
  pending: { label: 'Pending', color: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  accepted: { label: 'Accepted', color: 'bg-green-50 text-green-700 border-green-200' },
  rejected: { label: 'Rejected', color: 'bg-red-50 text-red-700 border-red-200' },
};

export default function QuotationReview({ quotation, isOpen, onClose, onUpdate }) {
  const [activeTab, setActiveTab] = useState('items');
  const [itemStatuses, setItemStatuses] = useState({});
  const [rejectionReasons, setRejectionReasons] = useState({});
  const [overallNotes, setOverallNotes] = useState('');
  const [revisionNotes, setRevisionNotes] = useState('');
  const [showRevisionDialog, setShowRevisionDialog] = useState(false);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [versionHistory, setVersionHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = ['owner', 'staff', 'admin', 'project_manager'].includes(user.role);
  const isVendorQuotation = quotation?.is_vendor_quotation || quotation?.vendor_id;
  const canReview = isAdmin && isVendorQuotation && ['pending', 'sent'].includes(quotation?.status);
  const canConvertToBill = isAdmin && ['accepted', 'partially_accepted'].includes(quotation?.status) && !quotation?.linked_bill_id;

  useEffect(() => {
    if (quotation?.items) {
      // Initialize item statuses from existing data
      const initialStatuses = {};
      const initialReasons = {};
      quotation.items.forEach((item, idx) => {
        const itemId = item.id || String(idx);
        initialStatuses[itemId] = item.status || 'pending';
        if (item.rejection_reason) {
          initialReasons[itemId] = item.rejection_reason;
        }
      });
      setItemStatuses(initialStatuses);
      setRejectionReasons(initialReasons);
    }
  }, [quotation]);

  useEffect(() => {
    if (isOpen && quotation?.id) {
      fetchVersionHistory();
    }
  }, [isOpen, quotation?.id]);

  const fetchVersionHistory = async () => {
    try {
      const response = await axios.get(`${API}/quotations/${quotation.id}/history`);
      setVersionHistory(response.data.versions || []);
    } catch (error) {
      console.error('Failed to fetch version history');
    }
  };

  const handleItemStatusChange = (itemId, status) => {
    setItemStatuses(prev => ({ ...prev, [itemId]: status }));
    if (status !== 'rejected') {
      setRejectionReasons(prev => {
        const newReasons = { ...prev };
        delete newReasons[itemId];
        return newReasons;
      });
    }
  };

  const handleReasonChange = (itemId, reason) => {
    setRejectionReasons(prev => ({ ...prev, [itemId]: reason }));
  };

  const handleSubmitReview = async () => {
    // Validate all rejected items have reasons
    const rejectedWithoutReason = Object.entries(itemStatuses)
      .filter(([id, status]) => status === 'rejected' && !rejectionReasons[id]?.trim())
      .map(([id]) => id);

    if (rejectedWithoutReason.length > 0) {
      toast.error('Please provide rejection reasons for all rejected items');
      return;
    }

    setSubmitting(true);
    try {
      const itemUpdates = Object.entries(itemStatuses).map(([itemId, status]) => ({
        item_id: itemId,
        status,
        rejection_reason: status === 'rejected' ? rejectionReasons[itemId] : null
      }));

      await axios.post(`${API}/quotations/${quotation.id}/review-items`, {
        items: itemUpdates,
        overall_notes: overallNotes
      });

      toast.success('Quotation review submitted successfully');
      onUpdate?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestRevision = async () => {
    if (!revisionNotes.trim()) {
      toast.error('Please provide revision notes');
      return;
    }

    setSubmitting(true);
    try {
      const itemsToRevise = Object.entries(itemStatuses)
        .filter(([_, status]) => status === 'rejected')
        .map(([id]) => id);

      await axios.post(`${API}/quotations/${quotation.id}/request-revision`, {
        revision_notes: revisionNotes,
        items_to_revise: itemsToRevise
      });

      toast.success('Revision requested. Vendor has been notified.');
      setShowRevisionDialog(false);
      onUpdate?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request revision');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConvertToBill = async () => {
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/quotations/${quotation.id}/convert-to-bill`, {
        notes: `Converted from quotation ${quotation.quotation_number}`
      });

      toast.success(`Bill ${response.data.bill.invoice_number} created successfully`);
      setShowConvertDialog(false);
      onUpdate?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert to bill');
    } finally {
      setSubmitting(false);
    }
  };

  const acceptedCount = Object.values(itemStatuses).filter(s => s === 'accepted').length;
  const rejectedCount = Object.values(itemStatuses).filter(s => s === 'rejected').length;
  const pendingCount = Object.values(itemStatuses).filter(s => s === 'pending').length;
  const acceptedTotal = quotation?.items?.reduce((sum, item, idx) => {
    const itemId = item.id || String(idx);
    return itemStatuses[itemId] === 'accepted' ? sum + (item.amount || 0) : sum;
  }, 0) || 0;

  if (!quotation) return null;

  const statusConfig = STATUS_CONFIG[quotation.status] || STATUS_CONFIG.draft;
  const StatusIcon = statusConfig.icon;

  return (
    <>
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent className="w-full sm:max-w-3xl overflow-y-auto" side="right" data-testid="quotation-review-panel">
          <SheetHeader className="pb-4 border-b">
            <div className="flex items-center justify-between">
              <div>
                <SheetTitle className="text-2xl font-bold flex items-center gap-3">
                  {quotation.quotation_number}
                  {quotation.version > 1 && (
                    <Badge variant="outline" className="text-xs">v{quotation.version}</Badge>
                  )}
                </SheetTitle>
                <SheetDescription className="flex items-center gap-2 mt-1">
                  {isVendorQuotation ? (
                    <Badge variant="outline" className="bg-purple-50 text-purple-700">
                      <Users size={12} className="mr-1" /> From Vendor
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-blue-50 text-blue-700">
                      <Building2 size={12} className="mr-1" /> To Client
                    </Badge>
                  )}
                  <Badge className={statusConfig.color}>
                    <StatusIcon size={12} className="mr-1" />
                    {statusConfig.label}
                  </Badge>
                </SheetDescription>
              </div>
              <div className="text-right flex flex-col items-end gap-2">
                <div>
                  <p className="text-sm text-stone-500">Total</p>
                  <p className="text-2xl font-bold">₹{quotation.total?.toLocaleString()}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    try {
                      const response = await axios.get(`${API}/quotations/${quotation.id}/export/pdf`, { responseType: 'blob' });
                      const blob = new Blob([response.data], { type: 'application/pdf' });
                      window.open(window.URL.createObjectURL(blob), '_blank');
                    } catch { toast.error('Failed to preview PDF'); }
                  }}
                  data-testid="preview-pdf-review"
                >
                  <ExternalLink size={14} className="mr-1" /> Preview PDF
                </Button>
              </div>
            </div>
          </SheetHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="items">
                <FileText size={14} className="mr-1" /> Items ({quotation.items?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="history">
                <History size={14} className="mr-1" /> History
              </TabsTrigger>
              <TabsTrigger value="summary">
                <Receipt size={14} className="mr-1" /> Summary
              </TabsTrigger>
            </TabsList>

            {/* Items Tab - Review Interface */}
            <TabsContent value="items" className="mt-4 space-y-4">
              {/* Review Status Bar */}
              {canReview && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-amber-700 font-medium">Review Status:</span>
                      <span className="text-green-600">{acceptedCount} accepted</span>
                      <span className="text-red-600">{rejectedCount} rejected</span>
                      <span className="text-yellow-600">{pendingCount} pending</span>
                    </div>
                    <div className="text-sm text-amber-700">
                      Accepted Value: <span className="font-bold">₹{acceptedTotal.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Line Items */}
              <div className="space-y-3">
                {quotation.items?.map((item, idx) => {
                  const itemId = item.id || String(idx);
                  const itemStatus = itemStatuses[itemId] || 'pending';
                  const itemStatusConfig = ITEM_STATUS[itemStatus];

                  return (
                    <div 
                      key={itemId} 
                      className={`border rounded-lg overflow-hidden ${
                        itemStatus === 'accepted' ? 'border-green-200 bg-green-50/30' :
                        itemStatus === 'rejected' ? 'border-red-200 bg-red-50/30' :
                        'border-stone-200'
                      }`}
                    >
                      {/* Item Header */}
                      <div className="p-4 flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs text-stone-400">#{idx + 1}</span>
                            <Badge variant="outline" className={itemStatusConfig.color}>
                              {itemStatusConfig.label}
                            </Badge>
                            {item.category_name && (
                              <Badge variant="outline" className="text-xs">{item.category_name}</Badge>
                            )}
                          </div>
                          <h4 className="font-medium text-stone-900">
                            {item.description || item.task_title || 'Item'}
                          </h4>
                          {item.vendor_rate_id && (
                            <p className="text-xs text-purple-600 mt-1">Linked to vendor rate</p>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-4 text-sm">
                            <span>{item.quantity} {item.unit || 'Nos'}</span>
                            <span>× ₹{item.rate?.toLocaleString()}</span>
                          </div>
                          <p className="text-lg font-bold text-stone-900 mt-1">
                            ₹{item.amount?.toLocaleString()}
                          </p>
                        </div>
                      </div>

                      {/* Review Actions */}
                      {canReview && (
                        <div className="px-4 pb-4 space-y-3">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-stone-600 mr-2">Decision:</span>
                            <Button
                              size="sm"
                              variant={itemStatus === 'accepted' ? 'default' : 'outline'}
                              className={itemStatus === 'accepted' ? 'bg-green-600 hover:bg-green-700' : 'hover:bg-green-50 hover:text-green-600'}
                              onClick={() => handleItemStatusChange(itemId, 'accepted')}
                              data-testid={`accept-item-${itemId}`}
                            >
                              <Check size={14} className="mr-1" /> Accept
                            </Button>
                            <Button
                              size="sm"
                              variant={itemStatus === 'rejected' ? 'default' : 'outline'}
                              className={itemStatus === 'rejected' ? 'bg-red-600 hover:bg-red-700' : 'hover:bg-red-50 hover:text-red-600'}
                              onClick={() => handleItemStatusChange(itemId, 'rejected')}
                              data-testid={`reject-item-${itemId}`}
                            >
                              <X size={14} className="mr-1" /> Reject
                            </Button>
                            <Button
                              size="sm"
                              variant={itemStatus === 'pending' ? 'default' : 'outline'}
                              className={itemStatus === 'pending' ? 'bg-yellow-600 hover:bg-yellow-700' : 'hover:bg-yellow-50 hover:text-yellow-600'}
                              onClick={() => handleItemStatusChange(itemId, 'pending')}
                            >
                              <Clock size={14} className="mr-1" /> Pending
                            </Button>
                          </div>

                          {/* Rejection Reason */}
                          {itemStatus === 'rejected' && (
                            <div className="bg-red-50 p-3 rounded-lg">
                              <label className="text-sm text-red-700 font-medium block mb-1">
                                Rejection Reason *
                              </label>
                              <Textarea
                                placeholder="Why is this item being rejected?"
                                value={rejectionReasons[itemId] || ''}
                                onChange={(e) => handleReasonChange(itemId, e.target.value)}
                                className="border-red-200 focus:border-red-400"
                                rows={2}
                              />
                            </div>
                          )}
                        </div>
                      )}

                      {/* Show existing rejection reason for non-reviewers */}
                      {!canReview && item.rejection_reason && (
                        <div className="px-4 pb-4">
                          <div className="bg-red-50 p-3 rounded-lg">
                            <p className="text-sm text-red-700">
                              <strong>Rejection Reason:</strong> {item.rejection_reason}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Overall Notes */}
              {canReview && (
                <div className="bg-stone-50 p-4 rounded-lg">
                  <label className="text-sm text-stone-700 font-medium block mb-2">
                    <MessageSquare size={14} className="inline mr-1" />
                    Overall Notes (optional)
                  </label>
                  <Textarea
                    placeholder="Any additional comments for this quotation..."
                    value={overallNotes}
                    onChange={(e) => setOverallNotes(e.target.value)}
                    rows={3}
                  />
                </div>
              )}

              {/* Review Actions */}
              {canReview && (
                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="text-sm text-stone-500">
                    {acceptedCount + rejectedCount === quotation.items?.length
                      ? 'All items reviewed'
                      : `${pendingCount} item(s) still pending`}
                  </div>
                  <div className="flex gap-2">
                    {rejectedCount > 0 && (
                      <Button
                        variant="outline"
                        className="border-purple-200 text-purple-600 hover:bg-purple-50"
                        onClick={() => setShowRevisionDialog(true)}
                        data-testid="request-revision-btn"
                      >
                        <Edit2 size={14} className="mr-1" /> Request Revision
                      </Button>
                    )}
                    <Button
                      onClick={handleSubmitReview}
                      disabled={submitting || pendingCount === quotation.items?.length}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="submit-review-btn"
                    >
                      {submitting ? 'Submitting...' : 'Submit Review'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Convert to Bill */}
              {canConvertToBill && (
                <div className="flex items-center justify-between pt-4 border-t bg-green-50 -mx-4 px-4 py-4 mt-4">
                  <div>
                    <p className="font-medium text-green-800">Ready to convert to bill</p>
                    <p className="text-sm text-green-600">
                      {quotation.accepted_items_count || acceptedCount} accepted items worth ₹{(quotation.accepted_total || acceptedTotal).toLocaleString()}
                    </p>
                  </div>
                  <Button
                    onClick={() => setShowConvertDialog(true)}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="convert-to-bill-btn"
                  >
                    <Receipt size={14} className="mr-1" /> Convert to Bill
                  </Button>
                </div>
              )}

              {/* Linked Bill Info */}
              {quotation.linked_bill_id && (
                <div className="flex items-center justify-between pt-4 border-t bg-blue-50 -mx-4 px-4 py-4 mt-4">
                  <div>
                    <p className="font-medium text-blue-800">Already converted to bill</p>
                    <p className="text-sm text-blue-600">Bill Number: {quotation.linked_bill_number}</p>
                  </div>
                  <Badge className="bg-blue-100 text-blue-700">Converted</Badge>
                </div>
              )}
            </TabsContent>

            {/* History Tab */}
            <TabsContent value="history" className="mt-4">
              <div className="space-y-3">
                {versionHistory.length > 0 ? (
                  versionHistory.map((version, idx) => (
                    <div 
                      key={version.id}
                      className={`p-4 rounded-lg border ${version.is_current ? 'border-blue-200 bg-blue-50' : 'border-stone-200'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">v{version.version}</Badge>
                          <span className="font-medium">{version.quotation_number}</span>
                          {version.is_current && (
                            <Badge className="bg-blue-100 text-blue-700">Current</Badge>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="font-bold">₹{version.total?.toLocaleString()}</p>
                          <p className="text-xs text-stone-500">
                            {formatDate(new Date(version.created_at))}
                          </p>
                        </div>
                      </div>
                      <Badge className={STATUS_CONFIG[version.status]?.color || 'bg-stone-100'}>
                        {STATUS_CONFIG[version.status]?.label || version.status}
                      </Badge>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-stone-500">
                    <History size={32} className="mx-auto mb-2 opacity-50" />
                    <p>No revision history</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Summary Tab */}
            <TabsContent value="summary" className="mt-4">
              <div className="space-y-4">
                {/* Quotation Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-stone-50 rounded-lg">
                    <p className="text-sm text-stone-500">Project</p>
                    <p className="font-medium">{quotation.project_name || 'N/A'}</p>
                  </div>
                  <div className="p-4 bg-stone-50 rounded-lg">
                    <p className="text-sm text-stone-500">Valid Until</p>
                    <p className="font-medium">{formatDate(new Date(quotation.valid_until))}</p>
                  </div>
                  {quotation.vendor_id && (
                    <div className="p-4 bg-purple-50 rounded-lg col-span-2">
                      <p className="text-sm text-purple-500">Vendor</p>
                      <p className="font-medium text-purple-700">{quotation.vendor_name || quotation.vendor_id}</p>
                    </div>
                  )}
                </div>

                {/* Financial Summary */}
                <div className="bg-stone-50 p-4 rounded-lg space-y-2">
                  <div className="flex justify-between text-stone-600">
                    <span>Subtotal:</span>
                    <span>₹{quotation.subtotal?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-stone-600">
                    <span>GST ({quotation.tax_percentage || 0}%):</span>
                    <span>₹{quotation.tax_amount?.toLocaleString()}</span>
                  </div>
                  {quotation.discount_amount > 0 && (
                    <div className="flex justify-between text-red-600">
                      <span>Discount:</span>
                      <span>-₹{quotation.discount_amount?.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-lg border-t pt-2 text-stone-900">
                    <span>Grand Total:</span>
                    <span>₹{quotation.total?.toLocaleString()}</span>
                  </div>
                  {quotation.accepted_total > 0 && (
                    <div className="flex justify-between text-green-600 pt-2 border-t">
                      <span>Accepted Items Total:</span>
                      <span className="font-bold">₹{quotation.accepted_total?.toLocaleString()}</span>
                    </div>
                  )}
                </div>

                {/* Notes */}
                {quotation.notes && (
                  <div className="p-4 bg-amber-50 rounded-lg">
                    <p className="text-sm text-amber-700 font-medium mb-1">Notes</p>
                    <p className="text-stone-700">{quotation.notes}</p>
                  </div>
                )}

                {/* Revision Notes */}
                {quotation.revision_notes && (
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <p className="text-sm text-purple-700 font-medium mb-1">Revision Notes</p>
                    <p className="text-stone-700">{quotation.revision_notes}</p>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </SheetContent>
      </Sheet>

      {/* Request Revision Dialog */}
      <Dialog open={showRevisionDialog} onOpenChange={setShowRevisionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 size={20} className="text-purple-600" />
              Request Revision
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-stone-600">
              You have rejected {rejectedCount} item(s). Would you like to request the vendor to revise this quotation?
            </p>
            <div>
              <label className="text-sm font-medium text-stone-700 block mb-2">
                Revision Instructions *
              </label>
              <Textarea
                placeholder="Explain what changes are needed..."
                value={revisionNotes}
                onChange={(e) => setRevisionNotes(e.target.value)}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRevisionDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleRequestRevision}
              disabled={submitting || !revisionNotes.trim()}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {submitting ? 'Sending...' : 'Send Revision Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert to Bill Dialog */}
      <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt size={20} className="text-green-600" />
              Convert to Bill
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-stone-600">
              This will create a new bill/invoice from the accepted items in this quotation.
            </p>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex justify-between mb-2">
                <span className="text-green-700">Accepted Items:</span>
                <span className="font-bold">{quotation.accepted_items_count || acceptedCount}</span>
              </div>
              <div className="flex justify-between text-lg font-bold text-green-800">
                <span>Bill Amount:</span>
                <span>₹{(quotation.accepted_total || acceptedTotal).toLocaleString()}</span>
              </div>
            </div>
            <p className="text-sm text-stone-500">
              The bill will be linked to this quotation for audit purposes.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvertDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleConvertToBill}
              disabled={submitting}
              className="bg-green-600 hover:bg-green-700"
            >
              {submitting ? 'Creating...' : 'Create Bill'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
